import { atom } from 'jotai';
import { atomWithStorage, loadable } from 'jotai/utils';
import { getMapDefaultConfig } from 'components/map/atoms';
import { atomWithLoadable, mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type {
  AreasQueryResponse,
  AvailableAfesResponse,
  DevelopmentAreaQueryResponse,
  SearchResult,
} from 'types/api-responses';
import type { AfeCatalogResult } from 'pages/afe-catalog/types';
import type {
  CategoriesResponse,
  ExpenseEnumsResponse,
  ExpenseScenarioDetailResponse,
  UnitTypesResponse,
} from '../types';
import { type FormMode } from './utils';

// ─────────────────────────────────────
// Area lookups — shared master data
// ─────────────────────────────────────

/** Distinct area-geometry categories for the "Area Category" dropdown. */
export const [categoriesBaseAtom, categoriesLoadableAtom] = atomWithLoadable(
  async () => {
    const response = await request.get<CategoriesResponse>(
      '/queries/area_lookups/categories'
    );
    return response as CategoriesResponse;
  }
);

/** Distinct development-area unit types for the "Unit Type" dropdown. */
export const [unitTypesBaseAtom, unitTypesLoadableAtom] = atomWithLoadable(
  async () => {
    const response = await request.get<UnitTypesResponse>(
      '/queries/area_lookups/unit_types'
    );
    return response as UnitTypesResponse;
  }
);

// ─────────────────────────────────────
// Line-item enums — expense classes, contexts, amount types, subtypes.
// Sourced from the BE (single source of truth) rather than hardcoded.
// ─────────────────────────────────────

/** Line-item enum values + labels + context-aware subtype lists. */
export const [enumsBaseAtom, enumsLoadableAtom] = atomWithLoadable(async () => {
  const response = await request.get<ExpenseEnumsResponse>(
    '/expense_scenarios/enums'
  );
  return response as ExpenseEnumsResponse;
});

/** Selected area category. */
export const areaCategoryAtom = atom('');

/** Area search query. undefined = no fetch, '' = fetch all, 'text' = filter. */
export const areaQueryAtom = atom<string | undefined>(undefined);

/** Reactive area-geometry search — shared `/queries/area_geometries`. */
const areaSearchBaseAtom = atom(async (get) => {
  const category = get(areaCategoryAtom);
  const query = get(areaQueryAtom);
  if (!category || query === undefined) return null;

  const response = await request.post<
    SearchResult<AreasQueryResponse>,
    unknown
  >(
    '/queries/area_geometries',
    { category, text: query || undefined, page: 1, pageSize: 10 },
    new AbortController()
  );

  if (response.type !== 'success') {
    throw new Error('Failed to search area geometries');
  }
  return response.data;
});

export const areaSearchLoadableAtom = loadable(areaSearchBaseAtom);

/** Selected unit type. */
export const unitTypeAtom = atom('');

/** Unit search query. undefined = no fetch, '' = fetch all, 'text' = filter. */
export const unitQueryAtom = atom<string | undefined>(undefined);

/** Reactive development-area search — shared `/queries/development_areas`. */
const unitSearchBaseAtom = atom(async (get) => {
  const unitType = get(unitTypeAtom);
  const query = get(unitQueryAtom);
  if (!unitType || query === undefined) return null;

  const response = await request.post<
    SearchResult<DevelopmentAreaQueryResponse>,
    unknown
  >(
    '/queries/development_areas',
    { unit_type: unitType, text: query || undefined, page: 1, pageSize: 10 },
    new AbortController()
  );

  if (response.type !== 'success') {
    throw new Error('Failed to search development areas');
  }
  return response.data;
});

export const unitSearchLoadableAtom = loadable(unitSearchBaseAtom);

// ─────────────────────────────────────
// State / County lookups — shared master data
// ─────────────────────────────────────

interface StateResult {
  id: number;
  name: string;
  abbreviation: string;
}

interface CountyResult {
  id: number;
  name: string;
}

/** Full list of states (small) for the State picker. */
export const [statesBaseAtom, statesLoadableAtom] = atomWithLoadable(
  async () => {
    const response = await request.get<StateResult[]>('/states');
    if (response && typeof response === 'object' && 'error' in response) {
      throw new Error((response as { error: string }).error);
    }
    return response as StateResult[];
  }
);

/** County search query. undefined = no fetch, '' = fetch all, 'text' = filter. */
export const countyQueryAtom = atom<string | undefined>(undefined);

/** Reactive county search — shared `/queries/counties` (defaults to supported states). */
const countySearchBaseAtom = atom(async (get) => {
  const query = get(countyQueryAtom);
  if (query === undefined) return null;

  const response = await request.post<
    { results: CountyResult[] },
    { error: string }
  >(
    '/queries/counties',
    { query: { text: query || undefined, paginated: false } },
    new AbortController()
  );

  if (response.type !== 'success') {
    throw new Error('Failed to search counties');
  }
  return response.data.results;
});

export const countySearchLoadableAtom = loadable(countySearchBaseAtom);

export const expenseScenarioMapConfigAtom = atomWithStorage(
  'insights-expense-scenario-map-config',
  getMapDefaultConfig()
);

// ─────────────────────────────────────
// Geometry fetch — per area entry
// ─────────────────────────────────────

interface FetchGeometryParams {
  entryId: number;
  /** Only "area" and "unit" have geometry endpoints. State/county have none yet. */
  type: 'area' | 'unit';
  configurableId: number;
}

/** Cache of fetched geometries, keyed by area entry ID. */
export const areaGeometriesAtom = atom<Record<number, GeoJSON.Geometry>>({});

/**
 * Fetches geometry for a selected area/unit and stores it in the cache,
 * keyed by the area entry ID, so the map can render the selected package.
 * @param {FetchGeometryParams} params - The entry ID, geometry type, and configurable ID
 * @return {GeoJSON.Geometry} The fetched geometry
 */
export const [fetchGeometryAtom, fetchGeometryLoadableAtom] =
  mutationAtomWithLoadable(
    async (
      _get,
      set,
      params: FetchGeometryParams
    ): Promise<GeoJSON.Geometry> => {
      const path =
        params.type === 'area'
          ? `/area_geometries/${params.configurableId}`
          : `/development_areas/${params.configurableId}`;
      const response = await request.get<{ geometry: GeoJSON.Geometry }>(path);
      const geometry = (response as { geometry: GeoJSON.Geometry }).geometry;

      set(areaGeometriesAtom, (prev) => ({
        ...prev,
        [params.entryId]: geometry,
      }));

      return geometry;
    }
  );

// ─────────────────────────────────────
// Fetch scenario (edit mode)
// ─────────────────────────────────────

/**
 * Fetches a detailed expense scenario (with area configs + line items) by ID
 * for edit mode via GET /expense_scenarios/:id.
 * @param {number} id - The scenario ID
 * @return {ExpenseScenarioDetailResponse} The detailed scenario response
 */
export const [fetchScenarioAtom, fetchScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (_get, _set, id: number): Promise<ExpenseScenarioDetailResponse> => {
      const response = await request.get<ExpenseScenarioDetailResponse>(
        `/expense_scenarios/${id}`
      );

      if (!response || 'error' in response) {
        throw new Error(
          (response as { error: string })?.error ?? 'Failed to load scenario'
        );
      }

      return response;
    }
  );

// ─────────────────────────────────────
// Save scenario (create POST / update PATCH)
// ─────────────────────────────────────

interface SaveScenarioParams {
  mode: FormMode;
  /** Required for edit mode. */
  id?: number;
  /** Pre-built nested-attributes payload (see utils.buildCreate/UpdatePayload). */
  payload: Record<string, unknown>;
}

/**
 * Saves an expense scenario — POST /expense_scenarios in "create" mode,
 * PATCH /expense_scenarios/:id in "edit" mode. Surfaces BE validation errors.
 * @param {SaveScenarioParams} params - The mode, optional ID, and payload
 * @return {ExpenseScenarioDetailResponse} The saved scenario response
 */
export const [saveScenarioAtom, saveScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (
      _get,
      _set,
      params: SaveScenarioParams
    ): Promise<ExpenseScenarioDetailResponse> => {
      const url =
        params.mode === 'create'
          ? '/expense_scenarios'
          : `/expense_scenarios/${params.id}`;

      const method = params.mode === 'create' ? request.post : request.patch;

      const response = await method<ExpenseScenarioDetailResponse, unknown>(
        url,
        params.payload,
        new AbortController()
      );

      if (response.type !== 'success') {
        const data = response.type === 'error' ? response.data : undefined;
        const body = data as { errors?: { message: string }[] } | undefined;
        const message =
          body?.errors?.map((e) => e.message).join(', ') ||
          'Failed to save scenario';
        throw new Error(message);
      }

      return response.data;
    }
  );

// ─────────────────────────────────────
// Fallback AFE picker
// ─────────────────────────────────────

/**
 * Candidate AFEs for the area-level fallback picker, shaped as the
 * AvailableAfesResponse the shared Edit AFE Source modal already consumes.
 *
 * Sourced from the AFE catalog rather than /slots/:id/available_afes: an area
 * has no slot, and the catalog is the Elasticsearch-backed read that already
 * carries pre-computed cost totals — the expensive Postgres derivation is what
 * Query::AvailableAfes exists to avoid.
 *
 * One page is fetched and the modal filters it in memory, matching how the
 * modal's search already works.
 */
/**
 * The modal's AvailableAfe rows carry no AFE number, but the summary row shows
 * one — so the number is carried alongside, keyed by id. Structural typing lets
 * this pass straight through to the modal, which ignores the extra field.
 */
export interface FallbackAfeCandidates extends AvailableAfesResponse {
  afeNumbersById: Record<string, string | null>;
}

/**
 * Search text for the fallback AFE picker. The catalog holds far more AFEs than
 * one page, and the modal filters only what it has been given — so narrowing
 * happens server-side and this atom drives the refetch.
 */
export const afeQueryAtom = atom<string>('');

export const [afeCatalogBaseAtom, afeCatalogLoadableAtom] = atomWithLoadable(
  async (get): Promise<FallbackAfeCandidates> => {
    const query = get(afeQueryAtom).trim();
    const response = await request.post<
      SearchResult<AfeCatalogResult>,
      unknown
    >(
      '/queries/afe_catalog',
      {
        page: 1,
        text: query || undefined,
        // Clamped to 100 server-side, so asking for more silently returns 100.
        pageSize: 100,
        // SORT_ATTRIBUTE_MAP keys are camelCase — 'afe_date' is rejected 422.
        sortAttribute: 'afeDate',
        sortDirection: 'DESC',
      },
      new AbortController()
    );

    if (response.type !== 'success') {
      throw new Error('Failed to load AFEs');
    }

    const results = response.data.results;

    return {
      afeNumbersById: Object.fromEntries(
        results.map((afe) => [String(afe.id), afe.afeNumber ?? null])
      ),
      // The modal shows `label` instead, and uses slotId only to decide whether
      // to reset its selection — so a stable non-empty placeholder is enough.
      slotId: 'expense-area-fallback',
      wellName: null,
      smartDefault: null,
      availableAfes: results.map((afe) => ({
        id: String(afe.id),
        afeType: afe.afeType ?? '',
        well: {
          id: afe.well?.slotId ?? '',
          name: afe.well?.name ?? '',
          href: afe.well?.href ?? undefined,
        },
        // The catalog returns null for an absent cost; AvailableAfe treats the
        // field as optional, so normalise null to undefined.
        drillingCost: afe.drillingCost ?? undefined,
        completionCost: afe.completionCost ?? undefined,
        facilitiesCost: afe.facilitiesCost ?? undefined,
        contingenciesCost: afe.contingenciesCost ?? undefined,
        otherCost: afe.otherCost ?? undefined,
        totalCost: afe.totalCost ?? undefined,
      })),
    };
  }
);
